import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Mail, Globe, Users, Database, Eye } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
              <p className="text-gray-600">How we protect and handle your data</p>
            </div>
          </div>
          
          <p className="text-sm text-gray-500">Last updated: January 21, 2025</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12 space-y-8">
        
        {/* Overview */}
        <Card>
          <CardHeader>
            <CardTitle>Our Commitment to Your Privacy</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none">
            <p>
              At Memara (EcoImpact Ventures Inc.), we are committed to protecting your privacy and handling your data with transparency. 
              This Privacy Policy explains how we collect, use, and protect your information when you use our AI memory services.
            </p>
          </CardContent>
        </Card>

        {/* Information We Collect */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-500" />
              Information We Collect
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Account Information</h4>
              <ul className="list-disc pl-6 space-y-1 text-gray-600">
                <li>Email address for account creation and authentication</li>
                <li>User preferences and settings</li>
                <li>Billing information for paid services</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Memory Data</h4>
              <ul className="list-disc pl-6 space-y-1 text-gray-600">
                <li>Content you save to your memory spaces</li>
                <li>Memory metadata (timestamps, sources, tags)</li>
                <li>Search queries and usage patterns</li>
              </ul>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-2">Technical Information</h4>
              <ul className="list-disc pl-6 space-y-1 text-gray-600">
                <li>API usage logs for service optimization</li>
                <li>Error logs for debugging and improvements</li>
                <li>Basic analytics for feature usage (anonymized)</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* How We Use Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-green-500" />
              How We Use Your Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Service Delivery</h4>
                <ul className="list-disc pl-6 space-y-1 text-gray-600">
                  <li>Provide AI memory services</li>
                  <li>Process and store your memories</li>
                  <li>Enable search and retrieval</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Account Management</h4>
                <ul className="list-disc pl-6 space-y-1 text-gray-600">
                  <li>Authenticate and secure your account</li>
                  <li>Process billing and subscriptions</li>
                  <li>Send service-related communications</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Improvement</h4>
                <ul className="list-disc pl-6 space-y-1 text-gray-600">
                  <li>Improve service quality and performance</li>
                  <li>Develop new features</li>
                  <li>Debug and resolve technical issues</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Legal Compliance</h4>
                <ul className="list-disc pl-6 space-y-1 text-gray-600">
                  <li>Comply with applicable laws</li>
                  <li>Respond to legal requests</li>
                  <li>Protect our rights and users' safety</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Protection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-500" />
              How We Protect Your Data
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Technical Safeguards</h4>
              <ul className="list-disc pl-6 space-y-1 text-gray-600">
                <li>End-to-end encryption for data in transit</li>
                <li>Encryption at rest for stored data</li>
                <li>Multi-tenant data isolation</li>
                <li>Regular security audits and monitoring</li>
              </ul>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-2">Access Controls</h4>
              <ul className="list-disc pl-6 space-y-1 text-gray-600">
                <li>Role-based access for employees</li>
                <li>Two-factor authentication support</li>
                <li>Regular access reviews</li>
                <li>Principle of least privilege</li>
              </ul>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-2">Infrastructure</h4>
              <ul className="list-disc pl-6 space-y-1 text-gray-600">
                <li>Hosted on AWS infrastructure</li>
                <li>Regular backups and disaster recovery</li>
                <li>Network security and monitoring</li>
                <li>Secure development practices</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Data Sharing */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-500" />
              Data Sharing and Disclosure
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none">
            <p className="mb-4">We do not sell, rent, or trade your personal information. We may share your information only in these limited circumstances:</p>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Service Providers</h4>
                <p className="text-gray-600">
                  We work with trusted third-party providers (cloud infrastructure, payment processing) who help us deliver our services. 
                  These providers are contractually obligated to protect your data.
                </p>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Legal Requirements</h4>
                <p className="text-gray-600">
                  We may disclose information if required by law, court order, or government request, or to protect our rights, 
                  users' safety, or prevent fraud.
                </p>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Business Transfers</h4>
                <p className="text-gray-600">
                  In the event of a merger, acquisition, or sale, your data may be transferred as part of the business assets, 
                  with the same privacy protections.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Your Rights */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-indigo-500" />
              Your Rights and Choices
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Access and Control</h4>
                <ul className="list-disc pl-6 space-y-1 text-gray-600">
                  <li>View and export your data</li>
                  <li>Update your account information</li>
                  <li>Delete memories and spaces</li>
                  <li>Close your account</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Privacy Controls</h4>
                <ul className="list-disc pl-6 space-y-1 text-gray-600">
                  <li>Opt out of marketing communications</li>
                  <li>Adjust data retention settings</li>
                  <li>Control third-party integrations</li>
                  <li>Request data portability</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-blue-800 text-sm">
                <strong>Important:</strong> When you delete your account, all your memories and data are permanently deleted from our systems. 
                This action cannot be undone.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Data Retention */}
        <Card>
          <CardHeader>
            <CardTitle>Data Retention</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none">
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Active Accounts</h4>
                <p className="text-gray-600">
                  We retain your data while your account is active and as needed to provide services.
                  Memories are not automatically deleted based on subscription tier.
                </p>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Deleted Accounts</h4>
                <p className="text-gray-600">
                  When you delete your account, we permanently delete all your data within 30 days, except for:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-600">
                  <li>Billing records (kept for legal and tax purposes)</li>
                  <li>Anonymized usage analytics (cannot be linked back to you)</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Children's Privacy */}
        <Card>
          <CardHeader>
            <CardTitle>Children's Privacy</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none">
            <p>
              Our services are not intended for children under 13 years of age. We do not knowingly collect personal information 
              from children under 13. If we become aware that a child under 13 has provided us with personal information, 
              we will delete such information immediately.
            </p>
          </CardContent>
        </Card>

        {/* International Transfers */}
        <Card>
          <CardHeader>
            <CardTitle>International Data Transfers</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none">
            <p>
              Your data may be processed in countries other than your country of residence. We ensure appropriate safeguards 
              are in place to protect your data when it is transferred internationally, including using standard contractual 
              clauses and working only with providers that maintain adequate data protection standards.
            </p>
          </CardContent>
        </Card>

        {/* Updates */}
        <Card>
          <CardHeader>
            <CardTitle>Changes to This Policy</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none">
            <p className="mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Posting the updated policy on our website</li>
              <li>Sending you an email notification</li>
              <li>Displaying a notice in our application</li>
            </ul>
            <p className="mt-4">
              Your continued use of our services after the effective date of the updated policy constitutes acceptance of the changes.
            </p>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-green-500" />
              Contact Us
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none">
            <p className="mb-4">
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </p>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="space-y-2">
                <p className="text-sm">
                  <strong>EcoImpact Ventures Inc. (Memara)</strong><br />
                  6811 Kenilworth Ave, Suite 500<br />
                  Riverdale, MD 20737<br />
                  United States
                </p>
                <p className="text-sm">
                  <strong>Email:</strong> privacy@memara.io<br />
                  <strong>Support:</strong> support@memara.io
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
